"use client";

import { FC } from "react";
import FooterAccordion from "./FooterAccordion";
import OurCompanies from "./OurCompanies";
import BFLLink from "@repo/shared-ui/BFLLink";
import SectionList from "./SectionList";
import Conditional from "@repo/shared-ui/Conditional";
import BFLImage from "@repo/shared-ui/BFLImage";
import { handleFooterAppDownloadClickDL } from "./FooterAppDownloadFunction";
import { IFooter } from "../../interfaces/IFooter.interface";
import {
  getChildByNodeName,
  getSingleChildByNodeName,
} from "@repo/shared-utils/common-utils";
export interface GlobalFooterProps {
  content: IFooter;
}

const GlobalFooter: FC<GlobalFooterProps> = ({ content }) => {
  const {
    downloadAppText,
    copyRightText,
    corporateLinksTitle,
    yearText,
    companyNameImage,
  } = content ?? {};

  const corporateSection =
    content?.children?.find((item: any) => item.nodeName === "corporateSection")
      ?.children ?? [];

  const columnLinks =
    content?.children?.find(
      (item: any) => item.columnTitle === "Application Forms",
    )?.children ?? [];
  const appDownload = getChildByNodeName(content, "appDownload");
  const corporateLinks = getChildByNodeName(content, "corporateLinks");

  const getSectionByTitle = (title: string) => {
    if (!Array.isArray(content?.children)) return undefined;
    return content.children.find(
      (item: any) => item.columnTitle === title && "children" in item,
    );
  };

  const getColumnLinksBySection = (sectionTitle: string, nodeName: string) => {
    const section = getSectionByTitle(sectionTitle);
    return (
      section?.children?.find(
        (child: any) => child.nodeName === nodeName && "children" in child,
      )?.children ?? []
    );
  };

  // Helper for Products Portfolio (flattened structure)
  const extractLinksFromSectionItem = (sectionItem: any) => {
    const sectionTitle = sectionItem.sectionTitle;
    const sectionLinksNode = (sectionItem.children ?? []).find(
      (c: any) => c.nodeName === "sectionLinks",
    );
    const links =
      (sectionLinksNode?.children ?? []).map((link: any) => ({
        linkTitle: link.linkTitle,
        linkUrl: link.linkUrl,
      })) ?? [];
    return {
      sectionTitle,
      links,
    };
  };

  const extractColumnListArr = (item: any) =>
    item.children?.filter((c: any) => c.nodeName === "columnList") ?? [];

  const getProductsPortfolioData = () => {
    const section = getSectionByTitle("Products Portfolio");
    if (!section || !Array.isArray(section.children)) return [];
    const columnLinksNode = section.children.find(
      (child: any) =>
        child.nodeName === "columnLinks" && Array.isArray(child.children),
    );
    if (!columnLinksNode) return [];

    // Flatten the nested structure
    return columnLinksNode?.children?.flatMap((item: any) => {
      const columnListArr = extractColumnListArr(item);
      return columnListArr.flatMap((columnList: any) =>
        (columnList.children ?? []).map(extractLinksFromSectionItem),
      );
    });
  };

  const reachUsData = getColumnLinksBySection("Reach Us", "columnLinks");
  const socialMediaIcons =
    getColumnLinksBySection("Reach Us", "socialMedia") || [];
  const calculatorData = getColumnLinksBySection("Calculators", "columnLinks");
  const legalData = getColumnLinksBySection("Legal", "columnLinks");
  const productsData = getProductsPortfolioData() ?? [];

  return (
    <div className="bg-bfl-blue">
      <FooterAccordion
        data-testid="footer-accordion"
        loanLinks={columnLinks}
        reachUsData={reachUsData}
        socialMediaIcons={socialMediaIcons}
        calculatorData={calculatorData}
        legalData={legalData}
        productsData={productsData}
      />
      <div className="g-4 flex flex-wrap justify-between p-4 lg:gap-0">
        {corporateSection.map((sectionItem: any, index: number) => (
          <SectionList
            key={
              sectionItem?.id ||
              sectionItem?.columnTitle ||
              `corporate-section-${index}`
            }
            data-testid="section-list"
            data={
              sectionItem?.children?.find(
                (child: any) => child.nodeName === "columnList",
              )?.children ?? []
            }
            className="w-[40%] md:w-[18%]"
          />
        ))}
        <OurCompanies
          corporateLinksTitle={corporateLinksTitle ?? ""}
          corporateLinks={corporateLinks}
        />
        <div className="flex w-[100%] flex-col items-center pt-[50px] text-center md:w-[18%] md:pt-0">
          <BFLImage
            className="hidden pb-[30px] pt-[20px] md:!block"
            src={companyNameImage ?? ""}
            alt="Bajaj"
            priority={false}
            loading="lazy"
            unoptimized={true}
            width={84}
            height={83.39}
          />
          <div className="mb-2 flex gap-8 text-3xl">
            {appDownload?.map((_icon: any, index: number) => (
              <BFLLink
                key={"appDownload-" + index}
                href={_icon?.platformDownloadUrl}
                onClick={() => {
                  // Track analytics for app download click
                  handleFooterAppDownloadClickDL(
                    "", // loginStatus
                    _icon?.platformDownloadUrl || "", // destination_url
                    _icon.platformTitleText || "", // platform (Android/iOS)
                    _icon.platformTitleText || "", // cta_text
                  );
                }}
              >
                <>
                  <Conditional if={_icon.platformTitleText === "Android"}>
                    <i className="icomoon before:content-['\e921']"></i>
                  </Conditional>
                  <Conditional if={_icon.platformTitleText === "iOS"}>
                    <i className="icomoon before:content-['\e922']"></i>
                  </Conditional>
                </>
              </BFLLink>
            ))}
          </div>
          <h3
            data-testid="download-text"
            className="mb-4 text-left text-sm md:mb-0 md:text-base"
          >
            {downloadAppText}
          </h3>
        </div>
        <h3
          data-testid="copyright-text"
          className="mr-2 flex w-full justify-center text-[12px] md:!justify-end md:pb-0"
        >
          {yearText} {new Date().getFullYear()}
          {copyRightText}
        </h3>
      </div>
    </div>
  );
};
export default GlobalFooter;
